// frontend/src/components/planner/EditItemModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { X, Calendar, DollarSign, Percent, CreditCard } from 'lucide-react';
import api from '../../utils/api';
import { useBusinessEntity } from '../../contexts/BusinessEntityContext';

// Static color mapping — Tailwind PurgeCSS requires complete class strings at build time
const COLOR_STYLES = {
  orange: { headerBg: 'bg-orange-600', submitBg: 'bg-orange-600', submitHover: 'hover:bg-orange-700' },
  red: { headerBg: 'bg-red-600', submitBg: 'bg-red-600', submitHover: 'hover:bg-red-700' },
  emerald: { headerBg: 'bg-emerald-600', submitBg: 'bg-emerald-600', submitHover: 'hover:bg-emerald-700' },
};

const FREQUENCIES = [
  { value: 'once', label: 'One-time' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

// Helper to get today's date string
function getTodayString() {
  return new Date().toISOString().split('T')[0];
}

export default function EditItemModal({ item, onClose, onSuccess }) {
  const { entities } = useBusinessEntity();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [name, setName] = useState(item.name || '');
  const [categoryId, setCategoryId] = useState(item.category_id?.toString() || '');
  const [amount, setAmount] = useState(Math.abs(parseFloat(item.amount) || 0).toString());
  const [frequency, setFrequency] = useState(item.frequency || 'monthly');
  const [nextDueDate, setNextDueDate] = useState(item.next_due_date || getTodayString());
  const [dayOfMonth, setDayOfMonth] = useState(item.day_of_month?.toString() || '');
  const [isVariable, setIsVariable] = useState(item.is_variable || false);
  const [isEnvelope, setIsEnvelope] = useState(item.is_envelope || false);
  const [startDate, setStartDate] = useState(item.start_date || getTodayString());
  const [bankAccountId, setBankAccountId] = useState(item.bank_account_id?.toString() || '');
  const [businessEntityId, setBusinessEntityId] = useState(
    item.business_entity_id ? String(item.business_entity_id) : ''
  );

  // Accounts filtered to the selected entity. business_accounts.bank_account_id
  // is UNIQUE, so each account has at most one entity; accounts without a link
  // are Personal.
  const filteredAccounts = useMemo(() => {
    const targetEntityId = businessEntityId === '' ? null : parseInt(businessEntityId, 10);
    return accounts.filter(a => (a.business_entity_id ?? null) === targetEntityId);
  }, [accounts, businessEntityId]);

  // Human label used in the "Select an account for <entity>" hint.
  const selectedEntityLabel = (() => {
    if (businessEntityId === '') return 'Personal';
    const entity = entities.find(e => String(e.id) === String(businessEntityId));
    return entity?.name || 'this entity';
  })();

  function handleEntityChange(e) {
    const nextEntityId = e.target.value;
    setBusinessEntityId(nextEntityId);
    // Defensive clear: if we can't positively confirm the currently-selected
    // account belongs to the new entity, drop the selection. This covers the
    // stale-closure window where `accounts` is still loading (find → undefined)
    // which would otherwise keep the old bank_account_id and produce a
    // semantically inconsistent row after submit.
    if (!bankAccountId) return;
    const targetEntityId = nextEntityId === '' ? null : parseInt(nextEntityId, 10);
    const currentAccount = accounts.find(a => String(a.id) === String(bankAccountId));
    if (!currentAccount || (currentAccount.business_entity_id ?? null) !== targetEntityId) {
      setBankAccountId('');
    }
  }
  
  // Debt-specific fields
  const [interestRate, setInterestRate] = useState(item.interest_rate?.toString() || '');
  const [remainingBalance, setRemainingBalance] = useState(item.remaining_balance?.toString() || '');
  const [minimumPayment, setMinimumPayment] = useState(item.minimum_payment?.toString() || '');
  const [debtSubtype, setDebtSubtype] = useState(item.debt_subtype || '');
  const [originalBalance, setOriginalBalance] = useState(item.original_balance?.toString() || '');
  const [maturityDate, setMaturityDate] = useState(item.maturity_date || '');

  // Debt subtypes configuration
  const DEBT_SUBTYPES = [
    { value: 'credit_card', label: 'Credit Card', isRevolving: true },
    { value: 'auto_loan', label: 'Auto Loan', isRevolving: false },
    { value: 'student_loan', label: 'Student Loan', isRevolving: false },
    { value: 'mortgage', label: 'Mortgage', isRevolving: false },
    { value: 'personal_loan', label: 'Personal Loan', isRevolving: false },
    { value: 'medical_debt', label: 'Medical Debt', isRevolving: false },
    { value: 'other', label: 'Other', isRevolving: false }
  ];

  const isRevolvingDebt = DEBT_SUBTYPES.find(d => d.value === debtSubtype)?.isRevolving ?? false;

  // Load accounts and categories on mount
  useEffect(() => {
    async function loadData() {
      try {
        const [accountsRes, categoriesRes] = await Promise.all([
          api.get('/api/planner/accounts'),
          api.get('/api/household/categories')
        ]);
        
        if (accountsRes.data.success) {
          setAccounts(accountsRes.data.accounts);
        }
        if (categoriesRes.data.categories) {
          setCategories(categoriesRes.data.categories);
        }
      } catch (err) {
        console.error('Error loading data:', err);
      }
    }
    loadData();
  }, []);

  const [itemType, setItemType] = useState(item.item_type || 'bill');

  const typeLabels = {
    bill: { title: 'Edit Bill', color: 'orange' },
    debt: { title: 'Edit Debt', color: 'red' },
    income: { title: 'Edit Income', color: 'emerald' }
  };

  const { title, color } = typeLabels[itemType] || typeLabels.bill;
  const styles = COLOR_STYLES[color] || COLOR_STYLES.emerald;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    // For variable items, require start_date; for fixed items, require next_due_date
    if (isVariable) {
      if (!startDate) {
        setError('Start date is required for variable items');
        return;
      }
    } else {
      if (!nextDueDate) {
        setError('Next due date is required');
        return;
      }
    }

    // Debt validation
    if (itemType === 'debt') {
      if (!debtSubtype) {
        setError('Please select a debt type');
        return;
      }
      const rate = parseFloat(interestRate);
      const balance = parseFloat(remainingBalance);
      
      if (isNaN(rate) || rate < 0) {
        setError('Please enter a valid interest rate');
        return;
      }
      if (isNaN(balance) || balance < 0) {
        setError('Please enter a valid remaining balance');
        return;
      }
    }

    try {
      setLoading(true);

      const payload = {
        name: name.trim(),
        item_type: itemType,
        category_id: categoryId ? parseInt(categoryId, 10) : null,
        amount: amountNum,
        frequency,
        is_variable: isVariable,
        is_envelope: itemType === 'bill' ? isEnvelope : false,
        next_due_date: isVariable ? null : nextDueDate,
        start_date: isVariable ? startDate : null,
        day_of_month: isVariable ? null : (dayOfMonth ? parseInt(dayOfMonth, 10) : null),
        bank_account_id: bankAccountId ? parseInt(bankAccountId, 10) : null,
        business_entity_id: businessEntityId ? parseInt(businessEntityId, 10) : null
      };

      // Add debt fields
      if (itemType === 'debt') {
        payload.interest_rate = parseFloat(interestRate) || 0;
        payload.remaining_balance = parseFloat(remainingBalance) || 0;
        payload.minimum_payment = parseFloat(minimumPayment) || amountNum;
        payload.debt_subtype = debtSubtype;
        payload.original_balance = originalBalance ? parseFloat(originalBalance) : null;
        payload.maturity_date = maturityDate || null;
      }

      const response = await api.put(`/api/planner/recurring-items/${item.id}`, payload);

      if (response.data.success) {
        onSuccess();
      } else {
        setError(response.data.error || 'Failed to update item');
      }
    } catch (err) {
      console.error('Error updating item:', err);
      setError(err.response?.data?.error || 'Failed to update item');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center sm:p-4 text-center sm:items-center">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        <div className="relative w-full min-h-full sm:min-h-0 sm:my-8 sm:w-full sm:max-w-lg sm:max-h-[90vh] bg-white text-left shadow-xl transition-all rounded-none sm:rounded-lg sm:overflow-hidden flex flex-col">
          {/* Header — sticky on mobile so close/title stays visible while the form scrolls with the page */}
          <div className={`${styles.headerBg} px-4 sm:px-6 py-4 flex-shrink-0 sticky top-0 z-10 sm:static`}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">{title}</h3>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -mr-2"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Form — natural height on mobile (no inner scroll); capped + scrollable on desktop */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:overflow-y-auto sm:flex-1">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <div className="flex gap-2">
                {[
                  { value: 'bill', label: 'Bill', activeClass: 'border-blue-500 text-blue-600 bg-blue-50' },
                  { value: 'debt', label: 'Debt', activeClass: 'border-orange-500 text-orange-600 bg-orange-50' },
                  { value: 'income', label: 'Income', activeClass: 'border-green-500 text-green-600 bg-green-50' }
                ].map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setItemType(t.value)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${
                      itemType === t.value
                        ? t.activeClass
                        : 'border-gray-300 text-gray-500 hover:border-gray-400'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Entity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entity
              </label>
              <select
                value={businessEntityId}
                onChange={handleEntityChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Personal</option>
                {entities.map(ent => (
                  <option key={ent.id} value={String(ent.id)}>{ent.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Changes future pending forecast items; history stays where it is.
              </p>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select a category...</option>
                {categories
                  .filter(cat => {
                    // For income items, only show "Income" category
                    if (itemType === 'income') {
                      return cat.name === 'Income';
                    }
                    // For bills/debts, hide "Income" category
                    return cat.name !== 'Income';
                  })
                  .map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
              </select>
            </div>

            {/* Bank Account */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {itemType === 'income' ? 'Deposits To' : 'Paid From'}
              </label>
              <select
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select an account...</option>
                {filteredAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.displayNameWithInstitution || acc.displayName}</option>
                ))}
              </select>
              {accounts.length > 0 && filteredAccounts.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  No accounts linked to {selectedEntityLabel}. Link one from Linked Accounts or choose a different entity.
                </p>
              )}
              {filteredAccounts.length > 0 && !bankAccountId && (
                <p className="text-xs text-gray-500 mt-1">
                  Select an account for {selectedEntityLabel}.
                </p>
              )}
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {itemType === 'debt' ? 'Payment Amount' : 'Amount'}
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>

            {/* Frequency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequency
              </label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                {FREQUENCIES.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            {/* Variable Timing Toggle */}
            <div className="flex items-center gap-3 py-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isVariable}
                  onChange={(e) => setIsVariable(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
              <div>
                <span className="text-sm font-medium text-gray-700">Variable timing</span>
                <p className="text-xs text-gray-500">Date isn't fixed (e.g., haircuts, groceries)</p>
              </div>
            </div>

            {/* Budget Envelope Toggle - only for bills */}
            {itemType === 'bill' && (
              <div className="flex items-center gap-3 py-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isEnvelope}
                    onChange={(e) => setIsEnvelope(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
                <div>
                  <span className="text-sm font-medium text-gray-700">Budget envelope</span>
                  <p className="text-xs text-gray-500">Track actual spending against this budget (e.g., $700 food allowance)</p>
                </div>
              </div>
            )}

            {/* Next Due Date - only for fixed items */}
            {!isVariable && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Next Due Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={nextDueDate}
                    onChange={(e) => setNextDueDate(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>
            )}

            {/* Start Date - only for variable items */}
            {isVariable && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">When to start including in projections</p>
              </div>
            )}

            {/* Day of Month for Monthly - only for fixed items */}
            {!isVariable && frequency === 'monthly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Day of Month (optional)
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  placeholder="e.g., 15"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            )}

            {/* Debt-specific fields */}
            {itemType === 'debt' && (
              <>
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Debt Details
                  </h4>
                  
                  {/* Debt Type */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Debt Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={debtSubtype}
                      onChange={(e) => setDebtSubtype(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      required
                    >
                      <option value="">Select debt type...</option>
                      {DEBT_SUBTYPES.map(dt => (
                        <option key={dt.value} value={dt.value}>{dt.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {isRevolvingDebt ? 'Revolving debt with compound interest' : debtSubtype ? 'Fixed-term loan with simple interest' : ''}
                    </p>
                  </div>

                  {/* Interest Rate */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Interest Rate (APR)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={interestRate}
                        onChange={(e) => setInterestRate(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                      <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                  </div>

                  {/* Remaining Balance */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Balance
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={remainingBalance}
                        onChange={(e) => setRemainingBalance(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  {/* Minimum Payment */}
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Minimum Payment
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={minimumPayment}
                        onChange={(e) => setMinimumPayment(e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  {/* Installment Loan Fields - only show for non-revolving debt */}
                  {debtSubtype && !isRevolvingDebt && (
                    <div className="border-t border-gray-100 pt-3 mt-3">
                      <p className="text-xs text-gray-500 mb-2">Optional: Track loan progress</p>
                      
                      {/* Original Balance */}
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Original Loan Amount
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={originalBalance}
                            onChange={(e) => setOriginalBalance(e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            placeholder="e.g., 25000.00"
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        </div>
                      </div>

                      {/* Maturity Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Payoff Date
                        </label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="date"
                            value={maturityDate}
                            onChange={(e) => setMaturityDate(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] text-base sm:text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`w-full sm:w-auto px-4 py-3 sm:py-2 ${styles.submitBg} text-white rounded-lg ${styles.submitHover} disabled:opacity-50 transition-colors min-h-[44px] text-base sm:text-sm font-medium`}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
